/*
 * PRD-037 — Wizard UNIFICADO "Preparar comercio" (app SEPARADA del partner).
 *
 * UN solo flujo que pre-llena TODA la preparación del comercio sobre el borrador para que el alta
 * del dueño sea de <1 min (solo confirmar). Antes eran flujos sueltos (Configurar + Cargar menú +
 * Invitar) que "parecían páginas y procesos separados"; ahora son los 7 pasos de un único WizardV4
 * con UNA sola sesión-draft (se abre una vez en `useDraftSession`, no por paso):
 *
 *   [1 General] [2 Tipo] [3 Dirección] [4 Entrega] [5 Horarios] [6 Menú] [7 Invitar]
 *
 * Estrategia de reuso (mismo patrón que el panel OnBoarding.tsx): la sesión-draft deja el JWT del
 * borrador en localStorage como token de comercio, así el GET/PUT `/v1/comercio`, el SmartMenu
 * (`/v1/categoria`, `/v1/producto`) y demás endpoints del panel quedan scoped al borrador. Los pasos
 * son componentes prop-driven alimentados por `useDraftCommerce` (estado SIN Redux). El paso de menú
 * monta `<SmartMenuOnboarding>` plegado (como OnBoarding.tsx) y el de invitar genera el enlace de
 * activación con `createClaimLink` (cliente partner dedicado, independiente del token-draft).
 *
 * NO se setea `onboarding_finish_date`: eso lo sigue escribiendo el wizard del panel cuando el dueño
 * pulsa "Finalizar" en su configuración inicial (se respeta el PRD).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { WizardV4, type WizardV4Step, type WizardV4Handle } from '@components/v4/Wizard';
import BusinessTypeV4 from '@pages/OnBoarding/steps/BusinessTypeV4';
import {
  validateAddressValues,
  validateBusinessTypeValues,
  validateCommerce,
} from '@pages/OnBoarding/utils/validators';
import {
  SmartMenuOnboarding,
  useHasExistingCatalog,
  type SmartMenuCompletionResult,
} from '@components/v4/onboarding/SmartMenuOnboarding';
import { useFetchCallback } from '@services/services';
import { createClaimLink } from '@services/partnerService';
import type { Category, Product } from '@interfaces/general';
import { TextInputV4 } from '@components/v4/Input/Input';
import { partnerEvent, partnerTag } from '../analytics';
import { usePartnerAuth } from '../PartnerAuthContext';
import { copyTextToClipboard } from '../clipboard';
import { useDraftSession } from '../useDraftSession';
import { useDraftCommerce } from '../useDraftCommerce';
import { ConfigureGeneralSection } from '../ConfigureGeneralSection';
import { ConfigureAddressSection } from '../ConfigureAddressSection';
import { ConfigureDeliverySection, validateDeliveryValues } from '../ConfigureDeliverySection';
import PartnerScheduleEditor from '../PartnerScheduleEditor';
import { Button, C, Card, ErrorNote, Muted, PageShell, Title } from '../ui';

// Identificadores de paso → permiten deep-link a un paso concreto vía `?step=` (p. ej. el atajo
// "Cargar menú" abre el wizard directamente en el paso de menú) sin romper el orden del flujo.
const STEP_IDS = ['general', 'tipo', 'direccion', 'entrega', 'horarios', 'menu', 'invitar'] as const;
export type StepId = (typeof STEP_IDS)[number];

// Catálogo EXISTENTE del borrador (categorías + productos) traído por axios directo — la app del
// partner NO monta Redux, así que NO se pueden usar useGetCategories/useGetProducts (acoplados al
// store). Solo se dispara cuando ya hay catálogo (`enabled`), para alimentar el matching WS-B del
// SmartMenu en su fase 'review-existing'.
const useDraftCatalog = (enabled: boolean) => {
  // Handler de 401 propio (mismo patrón que useDraftCommerce): la app del partner NO monta el
  // AuthProvider del panel, así que el `auth.logout()` interno de useFetchCallback es un no-op y no
  // limpia el token-draft expirado. Sin esto, un 401 en el fetch del catálogo dejaría el token
  // inválido en localStorage → toda request siguiente lo reusa y falla. Aquí limpiamos las mismas
  // llaves del draft que gestiona useDraftSession, dejando la sesión coherente con el resto de la app.
  const onUnauthorized = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('id_commerce');
  }, []);
  const { call: fetchCategories } = useFetchCallback<Category[]>('get', '/v1/categoria', 'v1', { onUnauthorized });
  const { call: fetchProducts } = useFetchCallback<Product[]>('get', '/v1/producto', 'v1', { onUnauthorized });
  const [categories, setCategories] = useState<Category[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  // Distingue "todavía no llegó" (existingProducts vacío mientras carga) de "falló y no va a
  // llegar" — sin esta señal, `existingCatalogLoading` en SmartMenuOnboarding no puede distinguir
  // ambos casos (los dos dejan `existingProducts.length === 0` para siempre) y queda en spinner
  // infinito cuando el fetch falla (network error, 500, etc.) en vez de degradar a crear todo.
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    let active = true;
    setFailed(false);
    Promise.all([fetchCategories(), fetchProducts()])
      .then(([cats, prods]) => {
        if (!active) return;
        setCategories(cats || []);
        setProducts(prods || []);
      })
      .catch(() => {
        if (active) setFailed(true);
      });
    return () => {
      active = false;
    };
  }, [enabled, fetchCategories, fetchProducts]);

  return { categories, products, failed };
};

// Paso [6 Menú]: monta el SmartMenu sobre la sesión-draft (igual que OnBoarding.tsx). Si el partner
// ya precargó productos, arranca en 'review-existing' mostrándolos como ya procesados; si no, en el
// uploader. Solo monta cuando es el paso activo (WizardV4 renderiza únicamente el componente del
// paso actual), así la verificación de catálogo no corre hasta que el partner llega al menú.
const MenuStep = ({ onDone }: { onDone: (result: SmartMenuCompletionResult) => void }) => {
  const { hasCatalog } = useHasExistingCatalog();
  const { categories, products, failed } = useDraftCatalog(hasCatalog === true);
  // Si el fetch del catálogo existente falló, degrada a "crear todo" (uploader) en vez de dejar
  // `alreadyHasCatalog=true` con un `existingProducts` que nunca llegará — eso deja atrapado al
  // partner en el spinner de 'review-existing' para siempre.
  const effectiveHasCatalog = hasCatalog === true && !failed;

  if (hasCatalog === null) {
    return <Muted>Verificando si el comercio ya tiene menú cargado...</Muted>;
  }

  return (
    <SmartMenuOnboarding
      skippable
      trackingSource="cargar_menu"
      alreadyHasCatalog={effectiveHasCatalog}
      existingCategories={effectiveHasCatalog ? categories : undefined}
      existingProducts={effectiveHasCatalog ? products : undefined}
      onComplete={onDone}
    />
  );
};

// Paso [7 Invitar]: genera/copia el enlace de activación (misma lógica inline que CommerceDetail).
// `createClaimLink` usa el cliente `partnerApi` dedicado (header `partner-token`), así que funciona
// dentro de la sesión-draft sin tocar el token-draft de localStorage.
// Paso secuencial (una primaria a la vez):
//  - Estado A (sin enlace): única primaria "Generar enlace de activación". El "Finalizar" del wizard
//    está oculto (hideNextButton) para no competir con esta acción.
//  - Estado B (enlace generado): enlace + copiar / compartir por WhatsApp y la primaria "Finalizar"
//    (cierra el wizard vía `onFinish` → vuelve al detalle). El "Anterior" del wizard se mantiene.
const InviteStep = ({ idComercio, onFinish }: { idComercio: string; onFinish: () => void }) => {
  const { token } = usePartnerAuth();
  const [link, setLink] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState(false);

  const generate = async () => {
    if (!token) return;
    setError(null);
    setCopied(false);
    setCopyError(false);
    setLoading(true);
    try {
      const res = await createClaimLink(token, idComercio);
      setLink(`${window.location.origin}/pre-registro/${res.token}`);
      partnerEvent('partner_claim_link_generated');
    } catch {
      setError('No se pudo generar el enlace de activación.');
    } finally {
      setLoading(false);
    }
  };

  // Clipboard API + fallback execCommand('copy') (mismo patrón que CommerceDetail.tsx). Sin esto,
  // un fallo silencioso (contexto inseguro, permiso denegado) dejaba el botón sin ningún feedback
  // visible ni forma de recuperar — ahora, si ambas vías fallan, se avisa explícitamente (el input
  // de arriba ya es seleccionable manualmente como último recurso).
  const copy = () => {
    if (!link) return;
    setCopyError(false);
    copyTextToClipboard(link, (ok) => {
      if (ok) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } else {
        setCopyError(true);
      }
    });
  };

  // Abre WhatsApp (app o web) con el enlace pre-cargado en el mensaje para que el partner
  // se lo reenvíe al dueño. `wa.me` sin número deja al partner elegir el destinatario.
  const shareWhatsApp = () => {
    if (!link) return;
    const text = `Activa tu comercio en Plick con este enlace (válido 72h): ${link}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <p style={{ fontSize: 13, color: C.muted, margin: 0 }}>
        Genera el enlace de activación y compártelo con el dueño por WhatsApp. Al abrirlo validará su teléfono con un
        código y activará su cuenta (válido 72h).
      </p>
      {!link ? (
        <Button onClick={generate} loading={loading} disabled={loading || !token}>
          {loading ? 'Generando...' : 'Generar enlace de activación'}
        </Button>
      ) : (
        <>
          <TextInputV4
            value={link}
            onChange={() => {}}
            readOnly
            onFocus={(e) => e.currentTarget.select()}
            aria-label="Enlace de activación"
          />
          {/* Acciones secundarias sobre el enlace — ninguna es la primaria del paso. */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <Button variant="secondary" onClick={copy}>
              {copied ? 'Copiado ✓' : 'Copiar enlace'}
            </Button>
            <Button variant="secondary" onClick={shareWhatsApp}>
              Compartir por WhatsApp
            </Button>
            <Button variant="ghost" onClick={generate} disabled={loading}>
              Generar otro
            </Button>
          </div>
          {copyError && (
            <ErrorNote>No se pudo copiar automáticamente. Selecciona el texto del campo de arriba y cópialo.</ErrorNote>
          )}
          {/* Única primaria del paso: cierra el wizard (sustituye al "Finalizar" oculto del wizard). */}
          <Button variant="primary" size="lg" fullWidth onClick={onFinish}>
            Finalizar
          </Button>
        </>
      )}
      {error && <ErrorNote>{error}</ErrorNote>}
    </div>
  );
};

// Carga el borrador y monta el wizard. Se renderiza SOLO cuando la sesión-draft ya está lista
// (token en localStorage), así el GET/PUT /v1/comercio quedan scoped al borrador.
// `menuDeepLink` = se entró por el atajo "Cargar menú" (`?step=menu`): al terminar el menú se
// vuelve al detalle en vez de auto-avanzar a Invitar (el partner solo quería cargar el menú).
const ConfigureInner = ({
  idComercio,
  initialStep,
  menuDeepLink,
}: {
  idComercio: string;
  initialStep: number;
  menuDeepLink: boolean;
}) => {
  const navigate = useNavigate();
  const { commerce, loading, saving, loadError, sessionExpired, handleUpdateValue, save } = useDraftCommerce();
  const [saveError, setSaveError] = useState<string | null>(null);
  // Handle imperativo del wizard: el paso de menú NO muestra "Siguiente" (sería un
  // segundo primario compitiendo con "Publicar mi menú" del SmartMenu); en su lugar,
  // cuando el SmartMenu notifica fin (publicó o saltó) avanzamos a Invitar por aquí.
  const wizardRef = useRef<WizardV4Handle>(null);

  // Analytics: wizard opened for this draft (one event per commerce). Runs once the draft session is
  // ready (ConfigureInner only mounts then), before any early-return render branch.
  useEffect(() => {
    partnerTag({ id_commerce: idComercio });
    partnerEvent('partner_wizard_open');
  }, [idComercio]);

  const backToDetail = useCallback(() => navigate(`/partner/comercios/${idComercio}`), [navigate, idComercio]);

  // Cierre del wizard: lo comparten el `onFinish` del WizardV4 y la primaria "Finalizar" que el
  // paso Invitar renderiza por su cuenta (el "Finalizar" del wizard queda oculto vía hideNextButton).
  const finishWizard = useCallback(() => {
    partnerEvent('partner_wizard_finished');
    navigate(`/partner/comercios/${idComercio}?prepared=1`);
  }, [navigate, idComercio]);

  // Persiste el borrador. En error fija el mensaje y RE-LANZA para que el wizard no avance de paso
  // (mismo contrato que handleSubmitChanges del panel).
  const persist = useCallback(async () => {
    try {
      await save();
      setSaveError(null);
    } catch (err) {
      setSaveError('No se pudo guardar la configuración. Revisa los datos e intenta de nuevo.');
      throw err;
    }
  }, [save]);

  // Persist + analytics per wizard step. Fires `partner_wizard_step_saved` ONLY when the save
  // succeeds (persist re-throws on error, which also blocks the wizard from advancing). `wizard_step`
  // is a single last-write-wins tag holding the id of the just-saved step.
  const persistStep = useCallback(
    async (stepId: StepId) => {
      await persist();
      partnerEvent('partner_wizard_step_saved');
      partnerTag({ wizard_step: stepId });
    },
    [persist],
  );

  // El SmartMenu persiste su propio resultado server-side; en el wizard es informativo (el paso de
  // menú no muta el Commerce, así que no requiere PUT al avanzar). El SmartMenu es un sub-flujo
  // autocontenido con sus propias CTAs ("Publicar mi menú" / "Guardar y terminar después"); su
  // `onComplete` es el ÚNICO disparador de avance del paso.
  //
  // Dos comportamientos según cómo se entró al paso de menú:
  //  - Deep-link "Cargar menú" (`?step=menu`, `menuDeepLink`): el partner solo quería cargar el menú.
  //    Al terminar volvemos al DETALLE del comercio en vez de empujarlo a Invitar ("Paso 7 de 7").
  //  - Flujo normal (recorriendo los 7 pasos): avanzamos a Invitar haya publicado o saltado el menú
  //    (es opcional/best-effort) — evitando un segundo "Siguiente" del wizard que competía con el
  //    primario del SmartMenu.
  const onMenuDone = useCallback(() => {
    if (menuDeepLink) {
      backToDetail();
      return;
    }
    wizardRef.current?.goNext();
  }, [menuDeepLink, backToDetail]);

  // Sesion-draft expirada (401): mensaje especifico — NO el generico de loadError/save. El token ya
  // se limpio en el handler de 401; el dueno debe reabrir el borrador desde el detalle.
  if (sessionExpired) {
    return (
      <Card>
        <Title>Tu sesión del borrador expiró</Title>
        <Muted>Vuelve al detalle del comercio y abre de nuevo &quot;Preparar comercio&quot; para continuar.</Muted>
        <Button variant="secondary" onClick={backToDetail} style={{ marginTop: 14 }}>
          Volver al comercio
        </Button>
      </Card>
    );
  }

  if (loadError) {
    return (
      <Card>
        <Title>No se pudo abrir el borrador</Title>
        <Muted>Intenta de nuevo o vuelve al detalle del comercio.</Muted>
        <Button variant="secondary" onClick={backToDetail} style={{ marginTop: 14 }}>
          Volver al comercio
        </Button>
      </Card>
    );
  }

  if (loading || !commerce) {
    return (
      <Card>
        <Title>Cargando configuración...</Title>
        <Muted>Abriendo el borrador del comercio.</Muted>
      </Card>
    );
  }

  const generalMissing = validateCommerce(commerce);
  const businessMissing = validateBusinessTypeValues(commerce);
  const addressMissing = validateAddressValues(commerce);
  const deliveryMissing = validateDeliveryValues(commerce);

  const steps: WizardV4Step[] = [
    {
      label: 'General',
      component: (
        <ConfigureGeneralSection
          commerce={commerce}
          missingValues={generalMissing}
          handleUpdateValue={handleUpdateValue}
        />
      ),
      onSubmit: () => persistStep('general'),
      disableNextButton: generalMissing.length > 0 || saving,
    },
    {
      label: 'Tipo de negocio',
      component: <BusinessTypeV4 commerce={commerce} handleUpdateValue={handleUpdateValue} />,
      onSubmit: () => persistStep('tipo'),
      disableNextButton: businessMissing.length > 0 || saving,
    },
    {
      label: 'Dirección',
      component: (
        <ConfigureAddressSection
          commerce={commerce}
          handleUpdateValue={handleUpdateValue}
          missingValues={addressMissing}
        />
      ),
      onSubmit: () => persistStep('direccion'),
      disableNextButton: addressMissing.length > 0 || saving,
    },
    {
      label: 'Entrega',
      component: <ConfigureDeliverySection commerce={commerce} handleUpdateValue={handleUpdateValue} />,
      onSubmit: () => persistStep('entrega'),
      disableNextButton: deliveryMissing.length > 0 || saving,
    },
    {
      label: 'Horarios',
      // Editor de horarios propio del partner (V4 inline, mobile-first): reemplaza al ScheduleManager
      // reusado del panel — que usaba `Input.Hours` de @plick/ui, cuyo ícono de reloj absoluto tapaba
      // el valor de la hora en el card angosto del wizard a 320px. Aquí el picker es un `<input
      // type=time>` nativo sin ícono superpuesto (valor siempre visible) y las acciones (activar día,
      // agregar rango, copiar a otros días) son controles con texto claro, usables en touch. Mismo
      // contrato de datos (`ScheduleItem[]` + autoSave) que el ScheduleManager del panel.
      component: (
        <div style={{ width: '100%', maxWidth: '100%' }}>
          <PartnerScheduleEditor
            autoSave
            schedules={commerce.horarios || []}
            onChange={(s) => handleUpdateValue('horarios', s)}
            customTitle="Horarios de atención"
          />
        </div>
      ),
      onSubmit: () => persistStep('horarios'),
      disableNextButton: saving,
    },
    {
      label: 'Menú',
      // [6 Menú]: paso best-effort. Mismo criterio de jerarquía de CTAs que el OnBoarding del panel:
      // se OCULTA el "Siguiente" del wizard (`hideNextButton`) para no tener dos primarios compitiendo
      // ("Publicar mi menú" del SmartMenu vs "Siguiente" del wizard). El SmartMenu es la única CTA
      // primaria; su "Guardar y terminar después" es el omitir claro. Al notificar `onComplete`
      // (publicó o saltó) avanzamos a Invitar vía `wizardRef.goNext()` (ver `onMenuDone`). "Anterior"
      // del wizard se mantiene (no depende de `hideNextButton`).
      component: <MenuStep onDone={onMenuDone} />,
      hideNextButton: true,
    },
    {
      label: 'Invitar',
      // Último paso. Mismo criterio anti doble-CTA: se OCULTA el "Finalizar" del wizard
      // (hideNextButton también cubre el botón Finalizar del último paso) para no competir con la
      // acción del paso. En estado A la única primaria es "Generar enlace"; en estado B el propio
      // InviteStep muestra "Finalizar" (→ finishWizard). "Anterior" del wizard se mantiene.
      component: <InviteStep idComercio={idComercio} onFinish={finishWizard} />,
      hideNextButton: true,
    },
  ];

  return (
    <>
      <div style={{ marginBottom: 14 }}>
        <Button variant="ghost" onClick={backToDetail}>
          ← Volver al comercio
        </Button>
      </div>
      <WizardV4
        ref={wizardRef}
        title="Preparar comercio"
        subtitle="Pre-llena la configuración del negocio paso a paso. Entre más prepares, más rápido será el alta del dueño (solo confirmar)."
        isLoading={saving}
        steps={steps}
        initialStep={initialStep}
        // Cada paso ya persiste al avanzar (Siguiente). El paso de menú avanza por su cuenta vía
        // `wizardRef.goNext()` cuando el SmartMenu termina; el paso Invitar cierra el flujo con su
        // propia primaria "Finalizar" (→ finishWizard). Se conserva `onFinish` por contrato del
        // WizardV4 (lo invocaría su "Finalizar" si no estuviera oculto).
        onFinish={finishWizard}
      />
      {saveError && <ErrorNote>{saveError}</ErrorNote>}
    </>
  );
};

const ConfigureCommerce = ({ initialStepId }: { initialStepId?: StepId }) => {
  const { id } = useParams<{ id: string }>();
  const { token } = usePartnerAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { ready, error } = useDraftSession(token, id);

  // El paso inicial sale del `?step=` (deep-link, p. ej. el atajo "Cargar menú") o del prop de la
  // ruta; si ninguno aplica, arranca en General (0). WizardV4 lo clampa al rango válido.
  const requestedStep = (searchParams.get('step') as StepId | null) || initialStepId;
  const initialStep = requestedStep ? Math.max(0, STEP_IDS.indexOf(requestedStep)) : 0;
  // Se entró por el atajo "Cargar menú" (`?step=menu`): al terminar el menú se vuelve al detalle en
  // vez de auto-avanzar a Invitar. Solo aplica al deep-link explícito de menú, no al flujo normal.
  const menuDeepLink = searchParams.get('step') === 'menu';

  if (error) {
    return (
      <PageShell>
        <Card>
          <Title>Preparar comercio</Title>
          <ErrorNote>{error}</ErrorNote>
          <Button variant="secondary" onClick={() => navigate('/partner')} style={{ marginTop: 14 }}>
            Volver al portal
          </Button>
        </Card>
      </PageShell>
    );
  }

  if (!ready || !id) {
    return (
      <PageShell>
        <Card>
          <Title>Preparando configuración...</Title>
          <Muted>Abriendo la sesión del borrador.</Muted>
        </Card>
      </PageShell>
    );
  }

  // El ThemeProvider que necesitan ScheduleManager/SmartMenu ya se monta en el root (PartnerApp).
  return (
    <PageShell>
      <ConfigureInner idComercio={id} initialStep={initialStep} menuDeepLink={menuDeepLink} />
    </PageShell>
  );
};

export default ConfigureCommerce;
